'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDrawingContextOptional } from './DrawingContext';
import { useCoordinateTransform, calculateBounds } from './useCoordinateTransform';
import type {
  DrawingShape,
  DrawingDataPoint,
  DrawingLineShape,
  DrawingRectShape,
  DrawingEllipseShape,
  DrawingFreehandShape,
  DrawingCurveShape,
  DrawingShapeStyle,
} from './types';
import { DEFAULT_SHAPE_STYLE, STROKE_DASH_PATTERNS } from './types';

// Generate unique ID
const generateId = () => `drawing-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

// Douglas-Peucker path simplification
function simplifyPath(points: DrawingDataPoint[], tolerance: number): DrawingDataPoint[] {
  if (points.length <= 2) return points;

  const getX = (p: DrawingDataPoint) => (p.x instanceof Date ? p.x.getTime() : p.x);

  // Find the point with maximum distance from line between first and last
  let maxDist = 0;
  let maxIndex = 0;

  const first = points[0];
  const last = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], first, last, getX);
    if (dist > maxDist) {
      maxDist = dist;
      maxIndex = i;
    }
  }

  // If max distance is greater than tolerance, recursively simplify
  if (maxDist > tolerance) {
    const left = simplifyPath(points.slice(0, maxIndex + 1), tolerance);
    const right = simplifyPath(points.slice(maxIndex), tolerance);
    return [...left.slice(0, -1), ...right];
  }

  return [first, last];
}

function perpendicularDistance(
  point: DrawingDataPoint,
  lineStart: DrawingDataPoint,
  lineEnd: DrawingDataPoint,
  getX: (p: DrawingDataPoint) => number
): number {
  const x = getX(point);
  const y = point.y;
  const x1 = getX(lineStart);
  const y1 = lineStart.y;
  const x2 = getX(lineEnd);
  const y2 = lineEnd.y;

  const A = x - x1;
  const B = y - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;

  let param = -1;
  if (lenSq !== 0) param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = x - xx;
  const dy = y - yy;

  return Math.sqrt(dx * dx + dy * dy);
}

// Get SVG dash array from style
function getDashArray(dash: DrawingShapeStyle['strokeDash']): string {
  switch (dash) {
    case 'dash':
      return '8,4';
    case 'dot':
      return '2,4';
    case 'dashdot':
      return '8,4,2,4';
    default:
      return 'none';
  }
}

interface DrawingCanvasProps {
  chartId: string;
  plotRef: React.RefObject<{ el: HTMLElement | null } | null>;
  xAxisType?: 'date' | 'numeric' | 'category';
  disabled?: boolean;
  // Optional: data points for snapping (used for intelligent point placement)
  dataPoints?: Array<{ x: number | Date; y: number }>;
  // Optional: enable/disable snapping (default: enabled with Shift key)
  enableSnapping?: boolean;
}

export const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  chartId,
  plotRef,
  xAxisType = 'numeric',
  disabled = false,
  dataPoints = [],
  enableSnapping = true,
}) => {
  const drawingContext = useDrawingContextOptional();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // State for drawing in progress
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ pixel: { x: number; y: number }; data: DrawingDataPoint } | null>(null);
  const [currentPoint, setCurrentPoint] = useState<{ pixel: { x: number; y: number }; data: DrawingDataPoint } | null>(null);
  const [freehandPoints, setFreehandPoints] = useState<Array<{ pixel: { x: number; y: number }; data: DrawingDataPoint }>>([]);

  // State for dragging control points
  const [isDraggingHandle, setIsDraggingHandle] = useState(false);
  const [draggedHandle, setDraggedHandle] = useState<{
    shapeId: string;
    handleType: 'start' | 'end' | 'cp1' | 'cp2' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'center';
  } | null>(null);

  // Coordinate transform
  const { pixelToData, dataToPixel } = useCoordinateTransform(
    plotRef as any,
    { xAxisType }
  );

  // Helper: Find closest data point for snapping (Shift key activates)
  const findClosestDataPoint = useCallback(
    (targetPoint: DrawingDataPoint, snapThreshold = 20): DrawingDataPoint | null => {
      if (!enableSnapping || dataPoints.length === 0) return null;

      let closestPoint: DrawingDataPoint | null = null;
      let minDistance = Infinity;

      const targetPixel = dataToPixel(targetPoint.x, targetPoint.y);
      if (!targetPixel) return null;

      for (const point of dataPoints) {
        const pointPixel = dataToPixel(point.x, point.y);
        if (!pointPixel) continue;

        const distance = Math.sqrt(
          Math.pow(pointPixel.x - targetPixel.x, 2) + Math.pow(pointPixel.y - targetPixel.y, 2)
        );

        if (distance < minDistance && distance < snapThreshold) {
          minDistance = distance;
          closestPoint = point;
        }
      }

      return closestPoint;
    },
    [enableSnapping, dataPoints, dataToPixel]
  );

  // Get dimensions
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      const plotElement = plotRef.current?.el;
      if (plotElement) {
        setDimensions({
          width: plotElement.clientWidth,
          height: plotElement.clientHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [plotRef]);

  // If no drawing context, don't render
  if (!drawingContext) {
    return null;
  }

  const {
    state,
    addShape,
    updateShape,
    setMode,
    addTempPoint,
    clearTempPoints,
    selectShape,
    deselectAll,
    setActiveChart,
    currentStyle,
  } = drawingContext;

  const { activeTool, mode, shapes, selectedShapeIds, tempPoints } = state;

  // Get shapes for this chart
  const chartShapes = drawingContext.getShapesForChart(chartId);

  // Handle clicking on a shape to select it
  const handleShapeClick = useCallback(
    (e: React.MouseEvent, shapeId: string) => {
      e.stopPropagation();
      if (activeTool === 'select') {
        if (e.shiftKey) {
          // Add to selection
          selectShape(shapeId);
        } else {
          // Replace selection
          deselectAll();
          selectShape(shapeId);
        }
      }
    },
    [activeTool, selectShape, deselectAll]
  );

  // Handle control point drag start
  const handleHandleMouseDown = useCallback(
    (e: React.MouseEvent, shapeId: string, handleType: typeof draggedHandle extends null ? never : NonNullable<typeof draggedHandle>['handleType']) => {
      e.stopPropagation();
      if (activeTool !== 'select') return;

      setIsDraggingHandle(true);
      setDraggedHandle({ shapeId, handleType });
      setActiveChart(chartId);
    },
    [activeTool, chartId, setActiveChart]
  );

  // Handle mouse move for dragging handles
  const handleHandleDrag = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!isDraggingHandle || !draggedHandle) return;

      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const pixelX = e.clientX - rect.left;
      const pixelY = e.clientY - rect.top;

      const dataPoint = pixelToData(pixelX, pixelY);
      if (!dataPoint) return;

      const shape = chartShapes.find(s => s.id === draggedHandle.shapeId);
      if (!shape) return;

      // Update the shape based on handle type
      switch (shape.type) {
        case 'point': {
          if (draggedHandle.handleType === 'center') {
            updateShape(shape.id, { position: dataPoint } as any);
          }
          break;
        }

        case 'line': {
          if (draggedHandle.handleType === 'start') {
            updateShape(shape.id, { start: dataPoint } as any);
          } else if (draggedHandle.handleType === 'end') {
            updateShape(shape.id, { end: dataPoint } as any);
          }
          break;
        }
        case 'curve': {
          if (draggedHandle.handleType === 'start') {
            updateShape(shape.id, { start: dataPoint } as any);
          } else if (draggedHandle.handleType === 'end') {
            updateShape(shape.id, { end: dataPoint } as any);
          } else if (draggedHandle.handleType === 'cp1') {
            updateShape(shape.id, { controlPoint1: dataPoint } as any);
          } else if (draggedHandle.handleType === 'cp2') {
            updateShape(shape.id, { controlPoint2: dataPoint } as any);
          }
          break;
        }
        case 'rect': {
          const rectShape = shape as DrawingRectShape;
          if (draggedHandle.handleType === 'topLeft') {
            updateShape(shape.id, {
              topLeft: { x: dataPoint.x, y: Math.max(dataPoint.y, rectShape.bottomRight.y) },
            } as any);
          } else if (draggedHandle.handleType === 'bottomRight') {
            updateShape(shape.id, {
              bottomRight: { x: dataPoint.x, y: Math.min(dataPoint.y, rectShape.topLeft.y) },
            } as any);
          }
          break;
        }
        case 'ellipse': {
          const ellipseShape = shape as DrawingEllipseShape;
          const centerX = ellipseShape.center.x instanceof Date ? ellipseShape.center.x.getTime() : ellipseShape.center.x;
          const pointX = dataPoint.x instanceof Date ? dataPoint.x.getTime() : dataPoint.x;

          if (draggedHandle.handleType === 'start' || draggedHandle.handleType === 'end') {
            // Horizontal handles - adjust radiusX
            updateShape(shape.id, { radiusX: Math.abs(pointX - centerX) } as any);
          } else if (draggedHandle.handleType === 'cp1' || draggedHandle.handleType === 'cp2') {
            // Vertical handles - adjust radiusY
            updateShape(shape.id, { radiusY: Math.abs(dataPoint.y - ellipseShape.center.y) } as any);
          }
          break;
        }
      }
    },
    [isDraggingHandle, draggedHandle, chartShapes, pixelToData, updateShape]
  );

  // Handle mouse up to stop dragging
  const handleHandleMouseUp = useCallback(() => {
    setIsDraggingHandle(false);
    setDraggedHandle(null);
  }, []);

  // Handle mouse down - only used for freehand drawing
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (disabled || !activeTool || activeTool === 'select') return;

      // Only freehand uses mouse down (drag-based)
      if (activeTool !== 'freehand') return;

      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const pixelX = e.clientX - rect.left;
      const pixelY = e.clientY - rect.top;

      // Try to get data coordinates, fall back to pixel coords
      const dataPoint = pixelToData(pixelX, pixelY) || { x: pixelX, y: pixelY };

      setActiveChart(chartId);
      setIsDrawing(true);
      setStartPoint({ pixel: { x: pixelX, y: pixelY }, data: dataPoint });
      setCurrentPoint({ pixel: { x: pixelX, y: pixelY }, data: dataPoint });
      setMode('drawing');
      setFreehandPoints([{ pixel: { x: pixelX, y: pixelY }, data: dataPoint }]);

      e.preventDefault();
      e.stopPropagation();
    },
    [disabled, activeTool, pixelToData, setActiveChart, chartId, setMode]
  );

  // Use ref to track startPoint for mouse move (avoids stale closure)
  const startPointRef = useRef(startPoint);
  startPointRef.current = startPoint;
  const isDrawingRef = useRef(isDrawing);
  isDrawingRef.current = isDrawing;

  // Handle mouse move - show preview (using refs to avoid stale closures)
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const pixelX = e.clientX - rect.left;
      const pixelY = e.clientY - rect.top;

      // For freehand, collect points while dragging
      if (activeTool === 'freehand' && isDrawingRef.current) {
        const dataPoint = pixelToData(pixelX, pixelY);
        setFreehandPoints((prev) => [...prev, { pixel: { x: pixelX, y: pixelY }, data: dataPoint || { x: pixelX, y: pixelY } }]);
        setCurrentPoint({ pixel: { x: pixelX, y: pixelY }, data: dataPoint || { x: pixelX, y: pixelY } });
        return;
      }

      // For click-to-place tools, always update preview after first click
      if (startPointRef.current && activeTool && activeTool !== 'select' && activeTool !== 'freehand') {
        const dataPoint = pixelToData(pixelX, pixelY);
        setCurrentPoint({ pixel: { x: pixelX, y: pixelY }, data: dataPoint || { x: pixelX, y: pixelY } });
      }
    },
    [activeTool, pixelToData]
  );

  // Handle click - click-to-place drawing (first click = start, second click = complete)
  const handleDrawingClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (disabled || !activeTool || activeTool === 'select' || activeTool === 'freehand') return;

      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const pixelX = e.clientX - rect.left;
      const pixelY = e.clientY - rect.top;

      // Try to get data coordinates, fall back to pixel coords if not ready
      let dataPoint = pixelToData(pixelX, pixelY) || { x: pixelX, y: pixelY };

      // Apply snapping if Shift key is held down
      if (e.shiftKey && enableSnapping) {
        const snappedPoint = findClosestDataPoint(dataPoint);
        if (snappedPoint) {
          dataPoint = snappedPoint;
        }
      }

      // Point marker - single click creates the point immediately
      if (activeTool === 'point') {
        setActiveChart(chartId);
        const now = Date.now();
        const id = generateId();

        const newShape: DrawingShape = {
          id,
          chartId,
          type: 'point',
          position: dataPoint,
          label: '',
          symbol: 'circle',
          symbolSize: 8,
          style: { ...currentStyle },
          bounds: {
            xMin: typeof dataPoint.x === 'number' ? dataPoint.x : dataPoint.x.getTime(),
            xMax: typeof dataPoint.x === 'number' ? dataPoint.x : dataPoint.x.getTime(),
            yMin: dataPoint.y,
            yMax: dataPoint.y,
          },
          createdAt: now,
          modifiedAt: now,
        };

        addShape(newShape);
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // First click - set start point
      if (!startPoint) {
        setActiveChart(chartId);
        setStartPoint({ pixel: { x: pixelX, y: pixelY }, data: dataPoint });
        setCurrentPoint({ pixel: { x: pixelX, y: pixelY }, data: dataPoint });
        setMode('drawing');
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Second click - complete the shape
      const now = Date.now();
      const id = generateId();
      const endPoint = { pixel: { x: pixelX, y: pixelY }, data: dataPoint };

      let newShape: DrawingShape | null = null;

      switch (activeTool) {
        case 'line': {
          newShape = {
            id,
            chartId,
            type: 'line',
            start: startPoint.data,
            end: endPoint.data,
            style: { ...currentStyle },
            bounds: calculateBounds([startPoint.data, endPoint.data]),
            createdAt: now,
            modifiedAt: now,
          } as DrawingLineShape;
          break;
        }

        case 'rect': {
          newShape = {
            id,
            chartId,
            type: 'rect',
            topLeft: {
              x: Math.min(
                startPoint.data.x instanceof Date ? startPoint.data.x.getTime() : startPoint.data.x,
                endPoint.data.x instanceof Date ? endPoint.data.x.getTime() : endPoint.data.x
              ),
              y: Math.max(startPoint.data.y, endPoint.data.y),
            },
            bottomRight: {
              x: Math.max(
                startPoint.data.x instanceof Date ? startPoint.data.x.getTime() : startPoint.data.x,
                endPoint.data.x instanceof Date ? endPoint.data.x.getTime() : endPoint.data.x
              ),
              y: Math.min(startPoint.data.y, endPoint.data.y),
            },
            style: { ...currentStyle },
            bounds: calculateBounds([startPoint.data, endPoint.data]),
            createdAt: now,
            modifiedAt: now,
          } as DrawingRectShape;
          break;
        }

        case 'ellipse': {
          const startX = startPoint.data.x instanceof Date ? startPoint.data.x.getTime() : startPoint.data.x;
          const endX = endPoint.data.x instanceof Date ? endPoint.data.x.getTime() : endPoint.data.x;
          newShape = {
            id,
            chartId,
            type: 'ellipse',
            center: {
              x: (startX + endX) / 2,
              y: (startPoint.data.y + endPoint.data.y) / 2,
            },
            radiusX: Math.abs(endX - startX) / 2,
            radiusY: Math.abs(endPoint.data.y - startPoint.data.y) / 2,
            style: { ...currentStyle },
            bounds: calculateBounds([startPoint.data, endPoint.data]),
            createdAt: now,
            modifiedAt: now,
          } as DrawingEllipseShape;
          break;
        }

        case 'curve': {
          const startX = startPoint.data.x instanceof Date ? startPoint.data.x.getTime() : startPoint.data.x;
          const endX = endPoint.data.x instanceof Date ? endPoint.data.x.getTime() : endPoint.data.x;
          const midX = (startX + endX) / 2;
          const midY = (startPoint.data.y + endPoint.data.y) / 2;
          const offsetY = Math.abs(endPoint.data.y - startPoint.data.y) * 0.5 || Math.abs(endX - startX) * 0.3;

          newShape = {
            id,
            chartId,
            type: 'curve',
            start: startPoint.data,
            end: endPoint.data,
            controlPoint1: { x: midX, y: startPoint.data.y - offsetY },
            controlPoint2: { x: midX, y: endPoint.data.y - offsetY },
            style: { ...currentStyle },
            bounds: calculateBounds([startPoint.data, endPoint.data]),
            createdAt: now,
            modifiedAt: now,
          } as DrawingCurveShape;
          break;
        }
      }

      if (newShape) {
        addShape(newShape);
      }

      // Reset state
      setStartPoint(null);
      setCurrentPoint(null);
      setMode('idle');

      e.preventDefault();
      e.stopPropagation();
    },
    [disabled, activeTool, pixelToData, startPoint, chartId, currentStyle, addShape, setActiveChart, setMode]
  );

  // Handle mouse up - only for freehand drawing
  const handleMouseUp = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      // Only handle freehand completion
      if (activeTool !== 'freehand' || !isDrawing || !startPoint) {
        return;
      }

      if (freehandPoints.length < 2) {
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentPoint(null);
        setFreehandPoints([]);
        setMode('idle');
        return;
      }

      const now = Date.now();
      const id = generateId();

      // Simplify the path
      const dataPoints = freehandPoints.map((p) => p.data);
      const simplified = simplifyPath(dataPoints, 0.5);

      const freehandShape: DrawingFreehandShape = {
        id,
        chartId,
        type: 'freehand',
        points: simplified,
        style: { ...currentStyle },
        bounds: calculateBounds(simplified),
        createdAt: now,
        modifiedAt: now,
      };

      addShape(freehandShape);

      // Reset state
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentPoint(null);
      setFreehandPoints([]);
      setMode('idle');
    },
    [isDrawing, startPoint, activeTool, chartId, currentStyle, freehandPoints, addShape, setMode]
  );

  // Handle click - routes to drawing or selection
  const handleClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      // For drawing tools (except freehand), use click-to-place
      if (activeTool && activeTool !== 'select' && activeTool !== 'freehand') {
        handleDrawingClick(e);
        return;
      }

      // For select tool, deselect on background click
      if (activeTool === 'select') {
        deselectAll();
      }
    },
    [activeTool, handleDrawingClick, deselectAll]
  );

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentPoint(null);
        setFreehandPoints([]);
        setMode('idle');
        deselectAll();
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedShapeIds.length > 0) {
        drawingContext.deleteSelectedShapes();
      }

      // Undo/Redo
      if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
        if (e.shiftKey) {
          drawingContext.redo();
        } else {
          drawingContext.undo();
        }
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setMode, deselectAll, selectedShapeIds, drawingContext]);

  // Render preview shape during drawing
  const renderPreview = () => {
    // For freehand: need isDrawing to be true (drag-based)
    // For click-to-place tools (line, rect, ellipse, curve): need startPoint after first click
    if (!startPoint || !currentPoint) return null;
    if (activeTool === 'freehand' && !isDrawing) return null;

    const style = {
      stroke: currentStyle.strokeColor,
      strokeWidth: currentStyle.strokeWidth,
      strokeDasharray: getDashArray(currentStyle.strokeDash),
      fill: currentStyle.fillColor || 'none',
      fillOpacity: currentStyle.fillOpacity || 0.1,
    };

    switch (activeTool) {
      case 'line':
        return (
          <line
            x1={startPoint.pixel.x}
            y1={startPoint.pixel.y}
            x2={currentPoint.pixel.x}
            y2={currentPoint.pixel.y}
            {...style}
          />
        );

      case 'rect': {
        const x = Math.min(startPoint.pixel.x, currentPoint.pixel.x);
        const y = Math.min(startPoint.pixel.y, currentPoint.pixel.y);
        const width = Math.abs(currentPoint.pixel.x - startPoint.pixel.x);
        const height = Math.abs(currentPoint.pixel.y - startPoint.pixel.y);
        return <rect x={x} y={y} width={width} height={height} {...style} />;
      }

      case 'ellipse': {
        const cx = (startPoint.pixel.x + currentPoint.pixel.x) / 2;
        const cy = (startPoint.pixel.y + currentPoint.pixel.y) / 2;
        const rx = Math.abs(currentPoint.pixel.x - startPoint.pixel.x) / 2;
        const ry = Math.abs(currentPoint.pixel.y - startPoint.pixel.y) / 2;
        return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} {...style} />;
      }

      case 'freehand': {
        if (freehandPoints.length < 2) return null;
        const d = freehandPoints
          .map((p, i) => (i === 0 ? `M ${p.pixel.x} ${p.pixel.y}` : `L ${p.pixel.x} ${p.pixel.y}`))
          .join(' ');
        return <path d={d} {...style} fill="none" />;
      }

      case 'curve': {
        // Simple quadratic curve preview
        const midX = (startPoint.pixel.x + currentPoint.pixel.x) / 2;
        const midY = (startPoint.pixel.y + currentPoint.pixel.y) / 2;
        const offsetY = Math.abs(currentPoint.pixel.y - startPoint.pixel.y) * 0.5;
        const d = `M ${startPoint.pixel.x} ${startPoint.pixel.y} Q ${midX} ${midY - offsetY} ${currentPoint.pixel.x} ${currentPoint.pixel.y}`;
        return <path d={d} {...style} fill="none" />;
      }

      default:
        return null;
    }
  };

  // Helper to render a draggable handle
  const renderHandle = (
    cx: number,
    cy: number,
    shapeId: string,
    handleType: 'start' | 'end' | 'cp1' | 'cp2' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'center',
    isControlPoint = false
  ) => (
    <circle
      cx={cx}
      cy={cy}
      r={6}
      fill={isControlPoint ? 'white' : '#6366f1'}
      stroke={isControlPoint ? '#6366f1' : 'white'}
      strokeWidth={2}
      style={{ cursor: 'grab' }}
      onMouseDown={(e) => handleHandleMouseDown(e, shapeId, handleType)}
    />
  );

  // Render existing shapes (during drawing/selection only, Plotly renders final shapes)
  const renderShape = (shape: DrawingShape) => {
    const isSelected = selectedShapeIds.includes(shape.id);
    const style = {
      stroke: shape.style.strokeColor,
      strokeWidth: shape.style.strokeWidth,
      strokeDasharray: getDashArray(shape.style.strokeDash),
      fill: shape.style.fillColor || 'none',
      fillOpacity: shape.style.fillOpacity || 0.1,
      cursor: activeTool === 'select' ? 'pointer' : 'default',
    };

    // Convert data coordinates to pixels for rendering
    const toPixel = (point: DrawingDataPoint) => {
      const result = dataToPixel(point.x, point.y);
      return result || { x: 0, y: 0 };
    };

    // Click handler for selecting shapes
    const onShapeClick = (e: React.MouseEvent) => handleShapeClick(e, shape.id);

    switch (shape.type) {
      case 'line': {
        const start = toPixel(shape.start);
        const end = toPixel(shape.end);
        return (
          <g key={shape.id} onClick={onShapeClick}>
            {/* Invisible wider line for easier clicking */}
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke="transparent"
              strokeWidth={Math.max(shape.style.strokeWidth + 10, 15)}
              style={{ cursor: 'pointer' }}
            />
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} {...style} />
            {isSelected && (
              <>
                {renderHandle(start.x, start.y, shape.id, 'start')}
                {renderHandle(end.x, end.y, shape.id, 'end')}
              </>
            )}
          </g>
        );
      }

      case 'rect': {
        const tl = toPixel(shape.topLeft);
        const br = toPixel(shape.bottomRight);
        const x = Math.min(tl.x, br.x);
        const y = Math.min(tl.y, br.y);
        const width = Math.abs(br.x - tl.x);
        const height = Math.abs(br.y - tl.y);
        return (
          <g key={shape.id} onClick={onShapeClick}>
            <rect x={x} y={y} width={width} height={height} {...style} style={{ ...style, cursor: 'pointer' }} />
            {isSelected && (
              <>
                {renderHandle(x, y, shape.id, 'topLeft')}
                {renderHandle(x + width, y, shape.id, 'topRight')}
                {renderHandle(x, y + height, shape.id, 'bottomLeft')}
                {renderHandle(x + width, y + height, shape.id, 'bottomRight')}
              </>
            )}
          </g>
        );
      }

      case 'ellipse': {
        const center = toPixel(shape.center);
        // Approximate radii in pixels
        const corner = toPixel({ x: (shape.center.x as number) + shape.radiusX, y: shape.center.y + shape.radiusY });
        const rx = Math.abs(corner.x - center.x);
        const ry = Math.abs(corner.y - center.y);
        return (
          <g key={shape.id} onClick={onShapeClick}>
            <ellipse cx={center.x} cy={center.y} rx={rx} ry={ry} {...style} style={{ ...style, cursor: 'pointer' }} />
            {isSelected && (
              <>
                {renderHandle(center.x + rx, center.y, shape.id, 'start')}
                {renderHandle(center.x - rx, center.y, shape.id, 'end')}
                {renderHandle(center.x, center.y + ry, shape.id, 'cp1')}
                {renderHandle(center.x, center.y - ry, shape.id, 'cp2')}
              </>
            )}
          </g>
        );
      }

      case 'freehand': {
        if (shape.points.length < 2) return null;
        const pixelPoints = shape.points.map(toPixel);
        const d = pixelPoints.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
        return (
          <g key={shape.id} onClick={onShapeClick}>
            {/* Invisible wider path for easier clicking */}
            <path d={d} stroke="transparent" strokeWidth={15} fill="none" style={{ cursor: 'pointer' }} />
            <path d={d} {...style} fill="none" />
            {isSelected && pixelPoints.length > 0 && (
              <>
                <circle cx={pixelPoints[0].x} cy={pixelPoints[0].y} r={5} fill="#6366f1" stroke="white" strokeWidth={2} />
                <circle
                  cx={pixelPoints[pixelPoints.length - 1].x}
                  cy={pixelPoints[pixelPoints.length - 1].y}
                  r={5}
                  fill="#6366f1"
                  stroke="white"
                  strokeWidth={2}
                />
              </>
            )}
          </g>
        );
      }

      case 'curve': {
        const start = toPixel(shape.start);
        const end = toPixel(shape.end);
        const cp1 = toPixel(shape.controlPoint1);
        const cp2 = toPixel(shape.controlPoint2);
        const d = `M ${start.x} ${start.y} C ${cp1.x} ${cp1.y} ${cp2.x} ${cp2.y} ${end.x} ${end.y}`;
        return (
          <g key={shape.id} onClick={onShapeClick}>
            {/* Invisible wider path for easier clicking */}
            <path d={d} stroke="transparent" strokeWidth={15} fill="none" style={{ cursor: 'pointer' }} />
            <path d={d} {...style} fill="none" />
            {isSelected && (
              <>
                {/* Control point lines */}
                <line x1={start.x} y1={start.y} x2={cp1.x} y2={cp1.y} stroke="#6366f1" strokeWidth={1} strokeDasharray="4,4" />
                <line x1={end.x} y1={end.y} x2={cp2.x} y2={cp2.y} stroke="#6366f1" strokeWidth={1} strokeDasharray="4,4" />
                {/* Endpoint handles */}
                {renderHandle(start.x, start.y, shape.id, 'start')}
                {renderHandle(end.x, end.y, shape.id, 'end')}
                {/* Control point handles - drag to adjust arc angle */}
                {renderHandle(cp1.x, cp1.y, shape.id, 'cp1', true)}
                {renderHandle(cp2.x, cp2.y, shape.id, 'cp2', true)}
              </>
            )}
          </g>
        );
      }

      case 'point': {
        const pos = toPixel(shape.position);
        const size = shape.symbolSize || 8;
        const symbol = shape.symbol || 'circle';

        return (
          <g key={shape.id} onClick={onShapeClick}>
            {/* Larger invisible hit area */}
            <circle
              cx={pos.x}
              cy={pos.y}
              r={Math.max(size + 5, 12)}
              fill="transparent"
              style={{ cursor: 'pointer' }}
            />

            {/* Visible marker */}
            {symbol === 'circle' && (
              <circle
                cx={pos.x}
                cy={pos.y}
                r={size}
                fill={shape.style.fillColor || shape.style.strokeColor}
                stroke={shape.style.strokeColor}
                strokeWidth={shape.style.strokeWidth}
              />
            )}
            {symbol === 'square' && (
              <rect
                x={pos.x - size}
                y={pos.y - size}
                width={size * 2}
                height={size * 2}
                fill={shape.style.fillColor || shape.style.strokeColor}
                stroke={shape.style.strokeColor}
                strokeWidth={shape.style.strokeWidth}
              />
            )}
            {symbol === 'cross' && (
              <>
                <line
                  x1={pos.x - size}
                  y1={pos.y}
                  x2={pos.x + size}
                  y2={pos.y}
                  stroke={shape.style.strokeColor}
                  strokeWidth={shape.style.strokeWidth}
                />
                <line
                  x1={pos.x}
                  y1={pos.y - size}
                  x2={pos.x}
                  y2={pos.y + size}
                  stroke={shape.style.strokeColor}
                  strokeWidth={shape.style.strokeWidth}
                />
              </>
            )}

            {/* Label */}
            {shape.label && (
              <text
                x={pos.x}
                y={pos.y - size - 5}
                textAnchor="middle"
                fontSize="12"
                fill={shape.style.strokeColor}
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                {shape.label}
              </text>
            )}

            {/* Selection indicator */}
            {isSelected && renderHandle(pos.x, pos.y, shape.id, 'center')}
          </g>
        );
      }

      default:
        return null;
    }
  };

  // Combined mouse move handler for drawing and dragging
  const handleCombinedMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (isDraggingHandle) {
        handleHandleDrag(e);
        return;
      }
      if (typeof handleMouseMove === 'function') {
        handleMouseMove(e);
      }
    },
    [isDraggingHandle, handleHandleDrag, handleMouseMove]
  );

  // Combined mouse up handler
  const handleCombinedMouseUp = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (isDraggingHandle) {
        handleHandleMouseUp();
        return;
      }
      handleMouseUp(e);
    },
    [isDraggingHandle, handleHandleMouseUp, handleMouseUp]
  );

  // Render overlay when a tool is active (don't wait for isReady - we can draw with pixel coords)
  const shouldRenderOverlay = activeTool !== null;

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: shouldRenderOverlay ? 10 : -1 }}
    >
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className={`absolute inset-0 ${shouldRenderOverlay ? 'pointer-events-auto' : 'pointer-events-none'}`}
        style={{
          cursor: isDraggingHandle ? 'grabbing' : activeTool === 'select' ? 'default' : activeTool ? 'crosshair' : 'default',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleCombinedMouseMove}
        onMouseUp={handleCombinedMouseUp}
        onMouseLeave={handleHandleMouseUp}
        onClick={handleClick}
      >
        {/* Render existing shapes */}
        {chartShapes.map(renderShape)}

        {/* Render preview during drawing */}
        {renderPreview()}
      </svg>
    </div>
  );
};

export default DrawingCanvas;
